import React, { useState, useEffect } from 'react';
import {
  Save, X, Plus, Trash2, ArrowRight, CheckCircle, XCircle,
  AlertCircle, GitBranch, Clock, Info, ChevronUp, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const WorkflowEditor = ({ workflow, onClose }) => {
  const { getProtectedTenantCode } = useTenantProtection();

  const [workflowName, setWorkflowName] = useState('');
  const [description, setDescription] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [steps, setSteps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  useEffect(() => {
    loadJobs();
    if (workflow) {
      loadWorkflowDetails();
    } else {
      // Initialize with one empty step for new workflows
      setSteps([createEmptyStep(0)]);
      setExpandedSteps(new Set([0]));
    }
  }, [workflow]);

  const loadJobs = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/jobs', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      setJobs(response.data.filter(job => job.IsEnabled));
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load jobs');
    }
  };

  const loadWorkflowDetails = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get(`/api/automation/workflows/${workflow.WorkflowID}`, {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      const data = response.data;
      setWorkflowName(data.WorkflowName);
      setDescription(data.Description || '');
      setIsEnabled(data.IsEnabled);
      setSteps(data.Steps || []);
      setExpandedSteps(new Set(data.Steps?.map((_, idx) => idx) || []));
    } catch (error) {
      console.error('Failed to load workflow details:', error);
      toast.error('Failed to load workflow details');
    }
  };

  const createEmptyStep = (order) => ({
    StepOrder: order,
    StepName: `Step ${order + 1}`,
    JobID: null,
    DelayBeforeExecution: 0,
    OnSuccess: 'next', // next, stop, goto:{stepOrder}
    OnFailure: 'stop', // stop, retry, goto:{stepOrder}
    RetryCount: 0,
    RetryDelay: 30000,
  });

  const handleAddStep = () => {
    const newStep = createEmptyStep(steps.length);
    setSteps([...steps, newStep]);
    setExpandedSteps(new Set([...expandedSteps, steps.length]));
  };

  const handleRemoveStep = (index) => {
    if (steps.length === 1) {
      toast.error('Workflow must have at least one step');
      return;
    }
    const newSteps = steps.filter((_, idx) => idx !== index);
    // Update step orders
    newSteps.forEach((step, idx) => {
      step.StepOrder = idx;
      step.StepName = step.StepName.replace(/Step \d+/, `Step ${idx + 1}`);
    });
    setSteps(newSteps);
  };

  const handleMoveStep = (index, direction) => {
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSteps.length) return;

    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    newSteps.forEach((step, idx) => {
      step.StepOrder = idx;
    });
    setSteps(newSteps);
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const toggleStepExpanded = (index) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSteps(newExpanded);
  };

  const validateWorkflow = () => {
    if (!workflowName.trim()) {
      toast.error('Workflow name is required');
      return false;
    }

    if (steps.length === 0) {
      toast.error('Workflow must have at least one step');
      return false;
    }

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (!step.JobID) {
        toast.error(`Step ${i + 1}: Please select a job`);
        return false;
      }

      // Validate goto targets
      if (step.OnSuccess?.startsWith('goto:')) {
        const targetOrder = parseInt(step.OnSuccess.split(':')[1]);
        if (isNaN(targetOrder) || targetOrder < 0 || targetOrder >= steps.length) {
          toast.error(`Step ${i + 1}: Invalid success goto target`);
          return false;
        }
      }
      if (step.OnFailure?.startsWith('goto:')) {
        const targetOrder = parseInt(step.OnFailure.split(':')[1]);
        if (isNaN(targetOrder) || targetOrder < 0 || targetOrder >= steps.length) {
          toast.error(`Step ${i + 1}: Invalid failure goto target`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateWorkflow()) return;

    try {
      setSaving(true);
      const tenantCode = getProtectedTenantCode();
      const data = {
        workflowName,
        description,
        isEnabled,
        steps,
      };

      if (workflow) {
        await axios.put(
          `/api/automation/workflows/${workflow.WorkflowID}`,
          data,
          { headers: { 'X-Tenant-Code': tenantCode } }
        );
        toast.success('Workflow updated successfully');
      } else {
        await axios.post('/api/automation/workflows', data, {
          headers: { 'X-Tenant-Code': tenantCode },
        });
        toast.success('Workflow created successfully');
      }
      onClose();
    } catch (error) {
      console.error('Failed to save workflow:', error);
      toast.error(error.response?.data?.error || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const getJobName = (jobId) => {
    const job = jobs.find(j => j.JobID === jobId);
    return job ? job.JobName : 'Select a job';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {workflow ? 'Edit Workflow' : 'Create Workflow'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Chain jobs together with conditional logic
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Workflow'}
          </button>
        </div>
      </div>

      {/* Workflow Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Workflow Settings
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Workflow Name *
            </label>
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-teal-500 focus:border-teal-500"
              placeholder="e.g., Daily Report Generation"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Describe what this workflow does..."
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isEnabled"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="isEnabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Enable workflow
            </label>
          </div>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Workflow Steps
          </h3>
          <button
            onClick={handleAddStep}
            className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Step
          </button>
        </div>

        <div className="space-y-4">
          {steps.map((step, index) => (
            <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              {/* Step Header */}
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => toggleStepExpanded(index)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    {expandedSteps.has(index) ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                  <GitBranch className="h-5 w-5 text-teal-500 dark:text-teal-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {step.StepName}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    → {getJobName(step.JobID)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {index > 0 && (
                    <button
                      onClick={() => handleMoveStep(index, 'up')}
                      className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      title="Move up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                  )}
                  {index < steps.length - 1 && (
                    <button
                      onClick={() => handleMoveStep(index, 'down')}
                      className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      title="Move down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveStep(index)}
                    className="p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    title="Remove step"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Step Content */}
              {expandedSteps.has(index) && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Step Name
                      </label>
                      <input
                        type="text"
                        value={step.StepName}
                        onChange={(e) => handleStepChange(index, 'StepName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Job to Execute *
                      </label>
                      <select
                        value={step.JobID || ''}
                        onChange={(e) => handleStepChange(index, 'JobID', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Select a job</option>
                        {jobs.map((job) => (
                          <option key={job.JobID} value={job.JobID}>
                            {job.JobName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Delay Before Execution (seconds)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={step.DelayBeforeExecution}
                      onChange={(e) => handleStepChange(index, 'DelayBeforeExecution', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  {/* Conditional Logic */}
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                      <GitBranch className="h-4 w-4 mr-2" />
                      Conditional Logic
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                          On Success
                        </label>
                        <select
                          value={step.OnSuccess}
                          onChange={(e) => handleStepChange(index, 'OnSuccess', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="next">Continue to next step</option>
                          <option value="stop">Stop workflow</option>
                          {steps.map((_, idx) => (
                            idx !== index && (
                              <option key={idx} value={`goto:${idx}`}>
                                Jump to Step {idx + 1}
                              </option>
                            )
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                          <XCircle className="h-4 w-4 mr-1 text-red-500" />
                          On Failure
                        </label>
                        <select
                          value={step.OnFailure}
                          onChange={(e) => handleStepChange(index, 'OnFailure', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="stop">Stop workflow</option>
                          <option value="retry">Retry this step</option>
                          {steps.map((_, idx) => (
                            idx !== index && (
                              <option key={idx} value={`goto:${idx}`}>
                                Jump to Step {idx + 1}
                              </option>
                            )
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Retry Settings */}
                    {step.OnFailure === 'retry' && (
                      <div className="mt-4 grid grid-cols-2 gap-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Retry Count
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={step.RetryCount}
                            onChange={(e) => handleStepChange(index, 'RetryCount', parseInt(e.target.value) || 1)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Retry Delay (ms)
                          </label>
                          <input
                            type="number"
                            min="1000"
                            step="1000"
                            value={step.RetryDelay}
                            onChange={(e) => handleStepChange(index, 'RetryDelay', parseInt(e.target.value) || 30000)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Workflow Flow Visualization */}
        {steps.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <Info className="h-4 w-4 mr-2" />
              Workflow Flow Preview
            </h4>
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              {steps.map((step, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <span className="font-medium text-teal-600 dark:text-teal-400">
                    {index + 1}.
                  </span>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {step.StepName}: {getJobName(step.JobID)}
                    </div>
                    <div className="text-xs ml-2 space-y-1 mt-1">
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                        Success: {step.OnSuccess === 'next' ? 'Continue' : step.OnSuccess === 'stop' ? 'Stop' : `Jump to Step ${parseInt(step.OnSuccess.split(':')[1]) + 1}`}
                      </div>
                      <div className="flex items-center">
                        <XCircle className="h-3 w-3 mr-1 text-red-500" />
                        Failure: {step.OnFailure === 'stop' ? 'Stop' : step.OnFailure === 'retry' ? `Retry ${step.RetryCount}x` : `Jump to Step ${parseInt(step.OnFailure.split(':')[1]) + 1}`}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowEditor;

